// Keyed DOM list reconciliation, shared by the catalog, thread replies, and
// reaction rows. Pure DOM: no app state, so it's unit-testable against a
// minimal node stub (tests/reconcile.test.js).

// Reconcile a list of keyed items into a parent element as children.
//   existing: Map<key, HTMLElement> of children currently in the parent
//   desired:  Array<{key, item, ctx?}> in desired order
//   create:   (item, ctx) => HTMLElement  — build a brand new element
//   update:   (el, item, ctx) => void     — update mutable parts only
//
// After this returns, 'existing' only contains entries for the surviving
// children (stale ones are removed from the map and from the DOM, and will
// be GC'd).
//
// Nodes that are already in the right place are left strictly alone. This used
// to build a fragment and replaceChildren() it, which detached and re-attached
// *every* card/post on every reconcile — enough to swallow a click that landed
// just before a background refresh or a load-more (the node under the pointer
// disappears between mousedown and mouseup, so no click event is ever
// delivered), and to drop hover state. Now only genuinely new or out-of-order
// nodes are touched; the common case of "same list, plus some appended" moves
// nothing that was already on screen.
//
// Children that aren't part of the keyed set (the unread line, an "(empty)"
// note) are removed, exactly as replaceChildren did — their owners re-insert
// them after this returns.
export function reconcileKeyedChildren(parent, existing, desired, create, update) {
  const seen = new Set();
  const ordered = [];

  for (const entry of desired) {
    const { key, item, ctx } = entry;
    seen.add(key);
    let el = existing.get(key);
    if (!el) {
      el = create(item, ctx);
    } else {
      update(el, item, ctx);
    }
    ordered.push(el);
  }

  for (const key of existing.keys()) {
    if (!seen.has(key)) existing.delete(key);
  }

  // Drop anything that shouldn't be here any more (removed items and
  // non-keyed children).
  const keep = new Set(ordered);
  for (const child of Array.from(parent.childNodes)) {
    if (!keep.has(child)) child.remove();
  }

  // Walk the desired order, only inserting where the DOM already differs.
  // insertBefore moves a node that's already attached, so out-of-order
  // survivors are repositioned without disturbing their neighbours.
  let ref = parent.firstChild;
  for (const el of ordered) {
    if (ref === el) {
      ref = el.nextSibling;
      continue;
    }
    parent.insertBefore(el, ref);
  }
}
